import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useVorgaenge } from "@/hooks/useVorgaenge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { safeAutoFocusRef } from "@/lib/safeAutoFocus";
import { findDuplicateTopicTitles } from "@/lib/universalDedupe";
import { AccountPolicyBanner, usePolicyBanner } from "@/components/AccountPolicyBanner";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Building2,
  Briefcase,
  MapPin,
  BookOpen,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Sparkles,
  Loader2,
  ShieldCheck,
  Wrench,
  AlertTriangle,
  Lightbulb,
  Activity,
  Library,
  Zap,
} from "lucide-react";

/**
 * SetupWizard (Phase 5 / Feb 2026)
 *
 * 4-Schritt-Onboarding, das beim ersten Login eines Betriebs erscheint und
 * NICHT übersprungen werden kann (User-Entscheidung Option C):
 *   1. Firma anlegen   (Firmenname + verantwortliche Person Pflicht, Adresse optional)
 *   2. Branche wählen  (Single-Select, Pilot: Tischlerei)
 *   3. Einsatzbereiche (Multi-Select, vorbelegt aus der KI-Freitext-Analyse)
 *   4. Themenwelten    (Multi-Select aus KI-Vorschlägen zum Freitext, vorbelegt)
 *
 * Nach Abschluss:
 *  - Firma wird gespeichert + `setup_completed=true` gesetzt
 *  - Gewählte Einsatzbereiche werden angelegt/wiederverwendet
 *  - Gewählte Themen werden parallel als Pending-Stubs angelegt; der
 *    `VorgangsTray` zeigt den Fortschritt automatisch.
 *
 * Beweissichere Designentscheidungen:
 *  - Flache Library-Architektur (kein Parent-Child wie Branche/Thema/Maschine)
 *  - Single-Select bei Branche (User-Anforderung)
 *  - Themen werden ad-hoc von der bestehenden KI-Pipeline generiert
 */

const STEPS = [
  { id: 1, label: "Firma", icon: Building2 },
  { id: 2, label: "Betrieb", icon: Briefcase },
  { id: 3, label: "Einsatzbereiche", icon: MapPin },
  { id: 4, label: "Welten", icon: BookOpen },
];

export default function SetupWizard({ onCompleted }) {
  const { optimisticAdd } = useVorgaenge();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const { policyError, handlePolicyError, clearPolicyError } = usePolicyBanner();

  // Step 1 – Firmenstamm
  const [companyName, setCompanyName] = useState("");
  const [companyAddress, setCompanyAddress] = useState("");
  const [contactPerson, setContactPerson] = useState("");

  // Step 2 – Betrieb beschreiben (Sprint L: KI-Vorschlagsliste statt fester Branche)
  const [businessDescription, setBusinessDescription] = useState("");
  const [suggesting, setSuggesting] = useState(false);
  const [suggestions, setSuggestions] = useState([]); // [{title,reason,category,priority}]
  const [suggestNote, setSuggestNote] = useState("");
  // Sprint L.1 – reine Anzeige: was die KI aus der Beschreibung erkannt hat.
  const [analysis, setAnalysis] = useState(null);
  // Punkt 5/6 (Juni 2026): „Weiter" in Schritt 2 erst nach gültiger KI-Analyse.
  // suggestedForDescription = exakter Text, für den die Vorschläge erzeugt
  // wurden. Weicht businessDescription davon ab → Vorschläge veraltet (Punkt 6).
  const [suggestedForDescription, setSuggestedForDescription] = useState(null);
  const [suggestError, setSuggestError] = useState("");

  // Step 3 – Einsatzbereiche (bestehende + eigene)
  const [existingEb, setExistingEb] = useState([]);
  // Map<key, {existing_id?, name?, selected}>: einheitliches Modell für beide
  const [ebItems, setEbItems] = useState([]);

  // Step 4 – Welten-Auswahl
  const [topics, setTopics] = useState({}); // title -> bool selected
  const [customTopics, setCustomTopics] = useState([]); // eigene Zusatzthemen
  // Universal-Welten (Juni 2026): ZUSÄTZLICHER Zweig – fertige, freigegebene
  // Welten des Master-Mandanten, standardmäßig vorangehakt (Kunde wählt ab).
  const [universalWorlds, setUniversalWorlds] = useState([]); // [{world_id,title,reason,category,priority}]
  const [universalSelected, setUniversalSelected] = useState({}); // world_id -> bool
  const [adoptProgress, setAdoptProgress] = useState(null); // {done,total}

  // Initialdaten laden.
  useEffect(() => {
    let cancelled = false;
    // Universal-Welten (erst in Schritt 4 gebraucht, ~4 s): früh starten, aber
    // ENTKOPPELT – Schritt 1–3 warten nicht darauf.
    api
      .listUniversalWorlds()
      .catch(() => null)
      .then((universal) => {
        if (cancelled) return;
        const uw = universal?.available ? universal.worlds || [] : [];
        setUniversalWorlds(uw);
        setUniversalSelected(Object.fromEntries(uw.map((w) => [w.world_id, true])));
      });
    (async () => {
      const [company, ebAll] = await Promise.all([
        api.getCompany().catch(() => null),
        api.listEinsatzbereiche(false).catch(() => []),
      ]);
      if (cancelled) return;
      if (company) {
        // Nur VORBELEGEN, falls noch leer – eine Eingabe, die der Nutzer
        // während des Ladens gemacht hat, gewinnt immer (Race-Fix 22.09.2026).
        setCompanyName((cur) => cur || company.name || "");
        setCompanyAddress((cur) => cur || company.address || "");
        setContactPerson((cur) => cur || company.contact_person || "");
      }
      const eb = (ebAll || []).filter((e) => !e.archived);
      setExistingEb(eb);
      // Bestehende Einsatzbereiche direkt als (aktive) Auswahl vorbelegen.
      setEbItems(
        eb.map((e) => ({
          key: `exist-${e.id}`,
          existing_id: e.id,
          name: e.name,
          selected: true,
          source: "existing",
        }))
      );
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Sprint L – KI-Startvorschläge aus der freien Betriebsbeschreibung erzeugen.
  // ERZEUGT KEINE Welten – nur eine Vorschlagsliste. Die Auswahl/Generierung
  // passiert erst beim Abschluss (Step 4 → /setup/complete, force_world=True).
  const handleSuggest = async () => {
    const desc = businessDescription.trim();
    if (!desc) {
      toast.error("Bitte beschreiben Sie zuerst Ihren Betrieb.");
      return;
    }
    setSuggesting(true);
    setSuggestError("");
    clearPolicyError();
    try {
      const res = await api.suggestWorlds(desc);
      const list = res?.suggestions || [];
      setSuggestions(list);
      setSuggestNote(res?.note || "");
      setAnalysis(res?.analysis || null);
      // Punkt 5: Vorschläge sind ab jetzt für GENAU diesen Text gültig → „Weiter".
      setSuggestedForDescription(desc);
      // Alle Vorschläge vorausgewählt; eigene Zusatzthemen bleiben erhalten.
      setTopics((prev) => {
        const next = {};
        for (const c of customTopics) next[c] = prev[c] ?? true;
        for (const s of list) next[s.title] = prev[s.title] ?? true;
        return next;
      });
      // Sprint L.1 – KI-Einsatzbereich-Vorschläge in die Auswahlliste mergen.
      // Bestehende + eigene Einträge bleiben erhalten; nur die KI-Vorschläge
      // werden ersetzt. Dedupe gegen vorhandene Namen (case-insensitive).
      const ebSuggest = res?.einsatzbereiche || [];
      setEbItems((prev) => {
        const kept = prev.filter((x) => x.source !== "suggested");
        const known = new Set(kept.map((x) => (x.name || "").toLowerCase()));
        const additions = [];
        for (const eb of ebSuggest) {
          const n = (eb?.name || "").trim();
          if (!n || known.has(n.toLowerCase())) continue;
          known.add(n.toLowerCase());
          additions.push({
            key: `sugg-${n}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
            name: n,
            reason: eb?.reason || "",
            selected: true,
            source: "suggested",
          });
        }
        return [...kept, ...additions];
      });
      toast.success(`${list.length} Startvorschläge erzeugt.`);
    } catch (e) {
      // Punkt 7: Fehler → NICHT weiter, kein gültiger Vorschlags-Stand.
      setSuggestedForDescription(null);
      // P5: Account-Sperre/Limit (z. B. KI-Kontingent) → eigenes Banner statt
      // generischem Fehlertext.
      if (!handlePolicyError(e)) {
        const msg =
          e?.response?.data?.detail || "Vorschläge konnten nicht erzeugt werden.";
        setSuggestError(msg);
        toast.error(msg);
      }
    } finally {
      setSuggesting(false);
    }
  };

  // Punkt 6: Ändert sich der Beschreibungstext nach einer Analyse, gelten die
  // Vorschläge als veraltet → Vorschläge/Auswahl zurücksetzen, „Weiter" sperren,
  // Button wieder auf „KI-Analyse starten".
  const onDescriptionChange = (val) => {
    setBusinessDescription(val);
    if (suggestedForDescription !== null && val.trim() !== suggestedForDescription) {
      setSuggestions([]);
      setAnalysis(null);
      setSuggestNote("");
      setSuggestError("");
      setSuggestedForDescription(null);
      // KI-Welten-Auswahl zurücksetzen (eigene Zusatzthemen bleiben erhalten).
      setTopics((prev) => {
        const next = {};
        for (const c of customTopics) next[c] = prev[c] ?? true;
        return next;
      });
      // KI-vorgeschlagene Einsatzbereiche stammten aus der nun veralteten
      // Analyse → entfernen (bestehende/eigene bleiben).
      setEbItems((arr) => arr.filter((x) => x.source !== "suggested"));
    }
  };

  const addCustomTopic = (name) => {
    const v = (name || "").trim();
    if (!v) return;
    if (!customTopics.some((c) => c.toLowerCase() === v.toLowerCase())) {
      setCustomTopics((arr) => [...arr, v]);
    }
    setTopics((m) => ({ ...m, [v]: true }));
  };

  const toggleTopic = (title) =>
    setTopics((m) => ({ ...m, [title]: !m[title] }));

  // Deduplizierung (22.09.2026): KI-Themen (Schritt 2), die sich thematisch mit
  // einer der verfügbaren Universal-Welten überschneiden, werden komplett aus der
  // Auswahl entfernt — die Universal-Welt bleibt die kanonische Quelle (kostenlos,
  // sofort). Abgleich gegen ALLE verfügbaren Universal-Welten (unabhängig vom Haken).
  const duplicateTopicTitles = useMemo(
    () =>
      findDuplicateTopicTitles(
        suggestions.map((s) => s.title),
        universalWorlds.map((w) => w.title)
      ),
    [suggestions, universalWorlds]
  );
  const visibleSuggestions = useMemo(
    () => suggestions.filter((s) => !duplicateTopicTitles.has(s.title)),
    [suggestions, duplicateTopicTitles]
  );
  const selectedTopicsList = useMemo(
    () =>
      Object.entries(topics)
        .filter(([k, v]) => v && !duplicateTopicTitles.has(k))
        .map(([k]) => k),
    [topics, duplicateTopicTitles]
  );
  const selectedUniversalList = useMemo(
    () => universalWorlds.filter((w) => universalSelected[w.world_id]),
    [universalWorlds, universalSelected]
  );
  const totalWorldCount = selectedTopicsList.length + selectedUniversalList.length;
  const toggleUniversal = (worldId) =>
    setUniversalSelected((m) => ({ ...m, [worldId]: !m[worldId] }));
  const selectedEbCount = useMemo(
    () => ebItems.filter((e) => e.selected).length,
    [ebItems]
  );

  // Punkt 5: „Weiter" in Schritt 2 nur bei gültigen Vorschlägen für den
  // AKTUELLEN Text (Punkt 6 invalidiert sie bei Textänderung automatisch).
  const hasValidSuggestions = useMemo(
    () =>
      suggestions.length > 0 &&
      suggestedForDescription !== null &&
      suggestedForDescription === businessDescription.trim(),
    [suggestions, suggestedForDescription, businessDescription]
  );

  // Validation pro Step
  const canAdvanceFrom = (s) => {
    if (s === 1) return companyName.trim().length > 0 && contactPerson.trim().length > 0;
    if (s === 2) return hasValidSuggestions; // Punkt 5: nur mit gültiger Analyse
    return true; // Step 3 & 4: dürfen leer sein
  };

  const goNext = () => {
    if (!canAdvanceFrom(step)) {
      if (step === 1) toast.error("Bitte den Firmennamen eintragen.");
      else if (step === 2)
        toast.error(
          businessDescription.trim()
            ? "Bitte zuerst die KI-Analyse starten."
            : "Bitte beschreiben Sie zuerst Ihren Betrieb."
        );
      return;
    }
    setStep((s) => Math.min(STEPS.length, s + 1));
  };

  const goBack = () => setStep((s) => Math.max(1, s - 1));

  const submit = async () => {
    if (!canAdvanceFrom(1)) {
      setStep(1);
      toast.error("Bitte Firmenname und verantwortliche Person eintragen.");
      return;
    }
    if (!canAdvanceFrom(2)) {
      setStep(2);
      toast.error("Bitte zuerst die KI-Analyse starten.");
      return;
    }
    // Punkt 5 + „keine Auto-Erzeugung ohne bewusste Auswahl": mind. eine Welt
    // (KI-Thema ODER Universal-Welt).
    if (totalWorldCount === 0) {
      setStep(STEPS.length);
      toast.error("Bitte wählen Sie mindestens eine Welt aus.");
      return;
    }
    setSubmitting(true);
    clearPolicyError();
    try {
      const payload = {
        company_name: companyName.trim(),
        company_address: companyAddress.trim(),
        contact_person: contactPerson.trim(),
        branche: "",
        business_description: businessDescription.trim(),
        einsatzbereiche: ebItems
          .filter((e) => e.selected)
          .map((e) =>
            e.existing_id
              ? { existing_id: e.existing_id }
              : { name: e.name }
          ),
        topics: selectedTopicsList,
      };
      const res = await api.completeSetup(payload);

      // Optimistische Tray-Anzeige für jedes Thema, damit der User die
      // Generierung direkt im Vorgangs-Chip sieht.
      const pendingIds = res?.pending_course_ids || [];
      pendingIds.forEach((id, idx) => {
        optimisticAdd({
          id,
          title: `Wird erstellt: ${selectedTopicsList[idx] || "Thema"}`,
          topic: selectedTopicsList[idx] || "",
        });
      });

      // Universal-Welten (Juni 2026): NACH dem unveränderten setup/complete,
      // je Welt ein kurzer Request (Kopie ohne KI, Fortschritt sichtbar).
      let adoptedCount = 0;
      const notAdopted = [];
      if (selectedUniversalList.length > 0) {
        for (let i = 0; i < selectedUniversalList.length; i += 1) {
          const w = selectedUniversalList[i];
          setAdoptProgress({ done: i, total: selectedUniversalList.length });
          try {
            const r = await api.adoptUniversalWorlds([w.world_id]);
            if ((r?.adopted || []).length > 0) adoptedCount += 1;
            else notAdopted.push(w.title);
          } catch (e) {
            notAdopted.push(w.title);
            if (handlePolicyError(e)) {
              // Sperre/Limit: restliche Universal-Welten nicht mehr versuchen.
              selectedUniversalList.slice(i + 1).forEach((x) => notAdopted.push(x.title));
              break;
            }
          }
        }
        setAdoptProgress(null);
      }

      // Abschluss-Gate (Variante A): totalWorldCount===0 ist hier unerreichbar.
      const parts = [];
      if (selectedTopicsList.length > 0) {
        parts.push(`${selectedTopicsList.length} Themen werden im Hintergrund erstellt.`);
      }
      if (adoptedCount > 0) {
        parts.push(
          `${adoptedCount} Universal-${adoptedCount === 1 ? "Welt" : "Welten"} sofort übernommen.`
        );
      }
      toast.success(`Setup abgeschlossen. ${parts.join(" ")}`.trim(), { duration: 6000 });
      if (notAdopted.length > 0) {
        toast.error(
          `${notAdopted.length} Universal-${notAdopted.length === 1 ? "Welt" : "Welten"} konnte${
            notAdopted.length === 1 ? "" : "n"
          } nicht übernommen werden: ${notAdopted.join(", ")}`,
          { duration: 10000 }
        );
      }
      onCompleted?.(res);
    } catch (e) {
      if (!handlePolicyError(e)) {
        toast.error(e?.response?.data?.detail || "Setup konnte nicht gespeichert werden.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-100/95 backdrop-blur-sm overflow-y-auto"
      data-testid="setup-wizard"
    >
      <div className="min-h-full flex items-start justify-center px-4 py-10">
        <div className="w-full max-w-3xl space-y-6">
          {/* Hero */}
          <div className="text-center" data-testid="setup-wizard-hero">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-900 text-white text-xs font-medium">
              <ShieldCheck className="w-3.5 h-3.5" />
              Einrichtung
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mt-4">
              Willkommen. In 4 Schritten startbereit.
            </h1>
            <p className="text-slate-600 mt-3 max-w-xl mx-auto">
              Beschreiben Sie Ihren Betrieb. CLartext.io erstellt daraus automatisch
              die wichtigsten Unterweisungen, Gefährdungsbeurteilungen und
              Betriebsanweisungen für Ihren Betrieb. Sie wählen aus, wir legen
              sie an.
            </p>
          </div>

          {/* Stepper */}
          <ol
            className="flex items-center justify-between gap-2"
            data-testid="setup-stepper"
          >
            {STEPS.map((s, idx) => {
              const isActive = step === s.id;
              const isDone = step > s.id;
              const Icon = s.icon;
              return (
                <li key={s.id} className="flex-1 flex items-center gap-2 min-w-0">
                  <div
                    className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm shrink-0 ${
                      isActive
                        ? "bg-slate-900 text-white"
                        : isDone
                          ? "bg-emerald-100 text-emerald-800"
                          : "bg-white text-slate-500 border border-slate-200"
                    }`}
                    data-testid={`setup-step-indicator-${s.id}`}
                  >
                    {isDone ? (
                      <CheckCircle2 className="w-4 h-4" />
                    ) : (
                      <Icon className="w-4 h-4" />
                    )}
                    <span className="font-medium hidden sm:inline">{s.label}</span>
                    <span className="font-medium sm:hidden">{s.id}</span>
                  </div>
                  {idx < STEPS.length - 1 && (
                    <div
                      className={`flex-1 h-px ${
                        isDone ? "bg-emerald-300" : "bg-slate-200"
                      }`}
                    />
                  )}
                </li>
              );
            })}
          </ol>

          {/* Step-Karte */}
          <Card
            className="bg-white border border-slate-200 shadow-sm p-6 sm:p-8"
            data-testid={`setup-step-card-${step}`}
          >
            {policyError && (
              <AccountPolicyBanner
                error={policyError}
                onDismiss={clearPolicyError}
                className="mb-6"
              />
            )}
            {step === 1 && (
              <StepFirma
                companyName={companyName}
                onCompanyName={setCompanyName}
                companyAddress={companyAddress}
                onCompanyAddress={setCompanyAddress}
                contactPerson={contactPerson}
                onContactPerson={setContactPerson}
              />
            )}
            {step === 2 && (
              <StepBetrieb
                description={businessDescription}
                onDescription={onDescriptionChange}
                suggestionCount={visibleSuggestions.length}
                hasValidSuggestions={hasValidSuggestions}
                suggestError={suggestError}
                note={suggestNote}
                analysis={analysis}
              />
            )}
            {step === 3 && (
              <StepEinsatzbereiche
                items={ebItems}
                onToggle={(key) =>
                  setEbItems((arr) =>
                    arr.map((x) =>
                      x.key === key ? { ...x, selected: !x.selected } : x
                    )
                  )
                }
                onAdd={(name) =>
                  setEbItems((arr) => [
                    ...arr,
                    {
                      key: `custom-${name}-${Date.now()}`,
                      name,
                      selected: true,
                      source: "custom",
                    },
                  ])
                }
              />
            )}
            {step === 4 && (
              <StepWelten
                suggestions={visibleSuggestions}
                customTopics={customTopics}
                topics={topics}
                onToggle={toggleTopic}
                onAddCustom={addCustomTopic}
                onBackToDescribe={() => setStep(2)}
                universalWorlds={universalWorlds}
                universalSelected={universalSelected}
                onToggleUniversal={toggleUniversal}
              />
            )}
          </Card>

          {/* Footer-Navigation */}
          <div
            className="flex items-center justify-between"
            data-testid="setup-footer"
          >
            <Button
              variant="outline"
              onClick={goBack}
              disabled={step === 1 || submitting}
              data-testid="setup-back-button"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Zurück
            </Button>
            <div className="text-xs text-slate-500">
              Schritt {step} von {STEPS.length}
            </div>
            {step === STEPS.length ? (
              <Button
                onClick={submit}
                disabled={submitting || totalWorldCount === 0}
                data-testid="setup-submit-button"
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {adoptProgress ? (
                      <span data-testid="setup-adopt-progress">
                        Universal-Welt {adoptProgress.done + 1}/{adoptProgress.total} wird übernommen…
                      </span>
                    ) : (
                      "Setup läuft…"
                    )}
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Setup abschließen
                    {totalWorldCount > 0 && (
                      <span className="ml-1.5">
                        ({totalWorldCount} Themen)
                      </span>
                    )}
                  </>
                )}
              </Button>
            ) : step === 2 && !hasValidSuggestions ? (
              <Button
                onClick={handleSuggest}
                disabled={!businessDescription.trim() || suggesting}
                data-testid="setup-suggest-button"
                className="bg-slate-900 hover:bg-slate-800 text-white"
              >
                {suggesting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    KI analysiert…
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    {suggestError ? "Erneut versuchen" : "Startvorschläge erzeugen"}
                  </>
                )}
              </Button>
            ) : (
              <Button
                onClick={goNext}
                disabled={!canAdvanceFrom(step) || submitting}
                data-testid="setup-next-button"
                className="bg-slate-900 hover:bg-slate-800 text-white"
              >
                Weiter
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>

          {/* Übersicht der aktuellen Auswahl unten */}
          {step >= 3 && (
            <div className="text-xs text-slate-500 text-center">
              <span data-testid="setup-summary">
                Einsatzbereiche:{" "}
                <strong className="text-slate-700">{selectedEbCount}</strong>
                {step >= 4 && (
                  <>
                    {" "}· Welten:{" "}
                    <strong className="text-slate-700">
                      {totalWorldCount}
                    </strong>
                  </>
                )}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Einzelne Schritte
// ---------------------------------------------------------------------------

function StepFirma({
  companyName,
  onCompanyName,
  companyAddress,
  onCompanyAddress,
  contactPerson,
  onContactPerson,
}) {
  return (
    <div className="space-y-5">
      <div>
        <h2 className="font-heading text-xl font-bold text-slate-900">
          Wie heißt Ihr Betrieb?
        </h2>
        <p className="text-sm text-slate-500 mt-1">
          Erscheint später automatisch auf jedem Nachweis. Firmenname und
          verantwortliche Person sind Pflicht, die Anschrift ist optional.
        </p>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="setup-firmenname">Firmenname *</Label>
        <Input
          id="setup-firmenname"
          value={companyName}
          onChange={(e) => onCompanyName(e.target.value)}
          placeholder="z. B. Tischlerei Müller GmbH"
          ref={safeAutoFocusRef}
          data-testid="setup-firmenname-input"
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="setup-adresse">Anschrift (optional)</Label>
        <Input
          id="setup-adresse"
          value={companyAddress}
          onChange={(e) => onCompanyAddress(e.target.value)}
          placeholder="z. B. Hauptstraße 1, 49074 Osnabrück"
          data-testid="setup-adresse-input"
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="setup-verantwortliche-person">Verantwortliche Person *</Label>
        <Input
          id="setup-verantwortliche-person"
          value={contactPerson}
          onChange={(e) => onContactPerson(e.target.value)}
          placeholder="z. B. Christian Lumpe"
          data-testid="setup-verantwortliche-person-input"
        />
        <p className="text-xs text-slate-500">
          Person, die im Betrieb für Arbeitsschutz und Unterweisungen verantwortlich ist.
        </p>
      </div>
    </div>
  );
}

function AnalysisChips({ icon: Icon, label, items, testid }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="space-y-1.5" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-xs font-medium text-slate-500">
        <Icon className="w-3.5 h-3.5" />
        {label}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {items.map((it) => (
          <span
            key={it}
            className="text-[11px] px-2 py-1 rounded-full bg-white border border-slate-200 text-slate-700"
          >
            {it}
          </span>
        ))}
      </div>
    </div>
  );
}

function AnalysisPanel({ analysis }) {
  const {
    detected_business_type: btype,
    detected_activities: activities,
    detected_work_equipment: equipment,
    detected_risks: risks,
    summary,
  } = analysis || {};
  const hasAny =
    btype ||
    summary ||
    (activities && activities.length) ||
    (equipment && equipment.length) ||
    (risks && risks.length);
  if (!hasAny) return null;
  return (
    <div
      className="rounded-lg border border-indigo-200 bg-indigo-50/60 p-4 space-y-3"
      data-testid="setup-analysis-panel"
    >
      <div className="flex items-center gap-2">
        <div className="w-7 h-7 rounded-md bg-indigo-600 text-white flex items-center justify-center">
          <Sparkles className="w-4 h-4" />
        </div>
        <div className="text-sm font-semibold text-slate-900">
          Die KI hat Ihren Betrieb analysiert
        </div>
        {btype && (
          <Badge
            className="bg-indigo-600 text-white hover:bg-indigo-600 text-[10px] uppercase tracking-wider"
            data-testid="setup-analysis-business-type"
          >
            {btype}
          </Badge>
        )}
      </div>
      {summary && (
        <p
          className="text-sm text-slate-600"
          data-testid="setup-analysis-summary"
        >
          {summary}
        </p>
      )}
      <div className="grid sm:grid-cols-3 gap-3">
        <AnalysisChips
          icon={Activity}
          label="Tätigkeiten"
          items={activities}
          testid="setup-analysis-activities"
        />
        <AnalysisChips
          icon={Wrench}
          label="Arbeitsmittel"
          items={equipment}
          testid="setup-analysis-equipment"
        />
        <AnalysisChips
          icon={AlertTriangle}
          label="Typische Gefährdungen"
          items={risks}
          testid="setup-analysis-risks"
        />
      </div>
      <p className="text-[11px] text-slate-400 flex items-center gap-1">
        <Lightbulb className="w-3 h-3" />
        Nur eine Einschätzung zur Orientierung – keine Rechtsberatung. Sie
        entscheiden, was angelegt wird.
      </p>
    </div>
  );
}

function StepBetrieb({
  description,
  onDescription,
  suggestionCount,
  hasValidSuggestions,
  suggestError,
  note,
  analysis,
}) {
  const examples = [
    "Wir sind ein Dachdeckerbetrieb mit PV-Montage.",
    "Wir betreiben eine Zahnarztpraxis.",
    "Wir sind ein Friseurbetrieb.",
    "Wir montieren Fenster und Haustüren.",
  ];
  return (
    <div className="space-y-5">
      <div>
        <h2 className="font-heading text-xl font-bold text-slate-900">
          Beschreiben Sie Ihren Betrieb
        </h2>
        <p className="text-sm text-slate-500 mt-1">
          Ein Satz genügt. Die KI erkennt Ihren Betrieb und schlägt daraus die
          passenden Unterweisungen, Gefährdungsbeurteilungen und
          Betriebsanweisungen vor – es wird noch nichts erzeugt.
        </p>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="setup-business-description">Was macht Ihr Betrieb?</Label>
        <textarea
          id="setup-business-description"
          value={description}
          onChange={(e) => onDescription(e.target.value)}
          rows={3}
          placeholder="z. B. Wir sind ein Dachdeckerbetrieb mit PV-Montage und 6 Mitarbeitern."
          className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900/20"
          data-testid="setup-business-description-input"
        />
        <div className="flex flex-wrap gap-1.5 pt-1">
          {examples.map((ex) => (
            <button
              key={ex}
              type="button"
              onClick={() => onDescription(ex)}
              className="text-[11px] px-2 py-1 rounded-full border border-slate-200 text-slate-500 hover:border-slate-400 hover:text-slate-700 transition-colors"
              data-testid="setup-business-example"
            >
              {ex}
            </button>
          ))}
        </div>
      </div>

      {!hasValidSuggestions && (
        <div
          className="rounded-md border border-slate-200 bg-slate-50 px-4 py-2.5 text-xs text-slate-600 flex items-start gap-2"
          data-testid="setup-suggest-hint"
        >
          <Sparkles className="w-3.5 h-3.5 mt-0.5 text-slate-400 shrink-0" />
          <span>
            Klicken Sie unten auf <strong>„Startvorschläge erzeugen"</strong>.
            Erst danach wird der Button zu <strong>„Weiter"</strong> – ohne
            KI-Analyse geht es nicht weiter.
          </span>
        </div>
      )}

      {suggestError && (
        <div
          className="rounded-md border border-rose-200 bg-rose-50 px-4 py-2.5 text-sm text-rose-800 flex items-start gap-2"
          data-testid="setup-suggest-error"
        >
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>
            {suggestError} Bitte unten auf <strong>„Erneut versuchen"</strong>{" "}
            klicken.
          </span>
        </div>
      )}

      {analysis && <AnalysisPanel analysis={analysis} />}

      {suggestionCount > 0 && (
        <div
          className="rounded-md border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-sm text-emerald-900"
          data-testid="setup-suggest-result"
        >
          {suggestionCount} Vorschläge bereit. Weiter zu „Einsatzbereiche" und
          „Welten", um auszuwählen.
        </div>
      )}
      {note && (
        <div
          className="rounded-md border border-amber-200 bg-amber-50 px-4 py-2.5 text-xs text-amber-900"
          data-testid="setup-suggest-note"
        >
          {note}
        </div>
      )}
    </div>
  );
}

function StepEinsatzbereiche({ items, onToggle, onAdd }) {
  const [custom, setCustom] = useState("");
  return (
    <div className="space-y-5">
      <div>
        <h2 className="font-heading text-xl font-bold text-slate-900">
          Wo arbeiten Ihre Mitarbeiter?
        </h2>
        <p className="text-sm text-slate-500 mt-1">
          Einsatzbereiche entscheiden später automatisch, wer welche Welt
          braucht. Die KI hat aus Ihrer Beschreibung passende Bereiche
          vorgeschlagen – wählen Sie ab oder ergänzen Sie eigene. Erst beim
          Abschluss werden sie gespeichert.
        </p>
      </div>
      <div className="space-y-2" data-testid="setup-einsatzbereiche-list">
        {items.map((it) => (
          <label
            key={it.key}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-md border transition-colors cursor-pointer ${
              it.selected
                ? "border-slate-300 bg-slate-50"
                : "border-slate-200 bg-white hover:bg-slate-50"
            }`}
            data-testid={`setup-eb-row-${it.key}`}
          >
            <Checkbox
              checked={it.selected}
              onCheckedChange={() => onToggle(it.key)}
              data-testid={`setup-eb-checkbox-${it.key}`}
            />
            <div className="flex-1 min-w-0">
              <div className="font-medium text-slate-900">{it.name}</div>
              {it.reason && (
                <div
                  className="text-xs text-slate-500 mt-0.5"
                  data-testid={`setup-eb-reason-${it.key}`}
                >
                  {it.reason}
                </div>
              )}
            </div>
            {it.existing_id ? (
              <Badge variant="outline" className="text-[10px] uppercase tracking-wider">
                bereits vorhanden
              </Badge>
            ) : it.source === "suggested" ? (
              <Badge
                className="bg-indigo-100 text-indigo-800 hover:bg-indigo-100 text-[10px] uppercase tracking-wider"
                data-testid={`setup-eb-suggested-badge-${it.key}`}
              >
                KI-Vorschlag
              </Badge>
            ) : (
              <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 text-[10px] uppercase tracking-wider">
                neu anlegen
              </Badge>
            )}
          </label>
        ))}
        {items.length === 0 && (
          <div className="text-sm text-slate-500 text-center py-6">
            Noch keine Einsatzbereiche – fügen Sie unten Ihre Bereiche hinzu
            (z. B. Werkstatt, Montage, Baustelle).
          </div>
        )}
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="setup-eb-custom">Eigenen Einsatzbereich hinzufügen</Label>
        <div className="flex gap-2">
          <Input
            id="setup-eb-custom"
            value={custom}
            onChange={(e) => setCustom(e.target.value)}
            placeholder="z. B. Außendienst, Reparaturteam"
            data-testid="setup-eb-custom-input"
          />
          <Button
            type="button"
            variant="outline"
            disabled={!custom.trim()}
            onClick={() => {
              const v = custom.trim();
              if (!v) return;
              onAdd(v);
              setCustom("");
            }}
            data-testid="setup-eb-custom-add"
          >
            Hinzufügen
          </Button>
        </div>
      </div>
    </div>
  );
}

function StepWelten({
  suggestions,
  customTopics,
  topics,
  onToggle,
  onAddCustom,
  onBackToDescribe,
  universalWorlds = [],
  universalSelected = {},
  onToggleUniversal,
}) {
  const [custom, setCustom] = useState("");
  const customRows = customTopics.map((t) => ({
    title: t,
    reason: "Eigenes Thema",
    category: "Eigenes",
    priority: "mittel",
  }));
  const rows = [...suggestions, ...customRows];
  const selectedCount = rows.filter((r) => !!topics[r.title]).length;
  const universalCount = universalWorlds.filter(
    (w) => universalSelected[w.world_id]
  ).length;

  const prioStyle = (p) =>
    p === "hoch"
      ? "bg-rose-100 text-rose-800"
      : p === "niedrig"
        ? "bg-slate-100 text-slate-600"
        : "bg-amber-100 text-amber-800";

  return (
    <div className="space-y-5">
      <div>
        <h2 className="font-heading text-xl font-bold text-slate-900">
          Welche Unterweisungen, Gefährdungsbeurteilungen und
          Betriebsanweisungen brauchen Sie sofort?
        </h2>
        <p className="text-sm text-slate-500 mt-1">
          Wir nennen diese vollständigen Dokumentationspakete im System
          „Welten". Wählen Sie die passenden Vorschläge aus – jede Welt wird als
          Standardvorlage erzeugt, die Sie anschließend prüfen und freigeben.
        </p>
      </div>

      <div
        className="rounded-md border border-amber-200 bg-amber-50 px-4 py-2.5 text-xs text-amber-900"
        data-testid="setup-welten-disclaimer"
      >
        <strong>Hinweis:</strong> Die KI erzeugt jede Welt als{" "}
        <em>Standardvorlage</em> (mit Gefährdungsbeurteilung + Betriebsanweisung).
        Erst nach Ihrer Freigabe sind Zuweisungen möglich.
      </div>

      {rows.length === 0 ? (
        <div
          className="text-sm text-slate-500 text-center py-6"
          data-testid="setup-welten-empty"
        >
          Noch keine Vorschläge.{" "}
          <button
            type="button"
            className="underline hover:text-slate-700"
            onClick={onBackToDescribe}
            data-testid="setup-welten-back-to-describe"
          >
            Zurück zur Betriebsbeschreibung
          </button>{" "}
          oder unten ein eigenes Thema hinzufügen.
        </div>
      ) : (
        <div className="space-y-2" data-testid="setup-welten-list">
          {rows.map((s) => {
            const selected = !!topics[s.title];
            return (
              <label
                key={s.title}
                className={`flex items-start gap-3 px-3 py-3 rounded-md border transition-colors cursor-pointer ${
                  selected
                    ? "border-slate-300 bg-slate-50"
                    : "border-slate-200 bg-white hover:bg-slate-50"
                }`}
                data-testid={`setup-welt-row-${slugifyTestId(s.title)}`}
              >
                <Checkbox
                  checked={selected}
                  onCheckedChange={() => onToggle(s.title)}
                  className="mt-0.5"
                  data-testid={`setup-welt-checkbox-${slugifyTestId(s.title)}`}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-slate-900">{s.title}</span>
                    {s.category && (
                      <Badge
                        variant="outline"
                        className="text-[10px] uppercase tracking-wider"
                      >
                        {s.category}
                      </Badge>
                    )}
                    {s.priority && (
                      <Badge
                        className={`${prioStyle(s.priority)} hover:opacity-100 text-[10px] uppercase tracking-wider`}
                      >
                        {s.priority}
                      </Badge>
                    )}
                  </div>
                  {s.reason && (
                    <div className="text-xs text-slate-500 mt-0.5">{s.reason}</div>
                  )}
                </div>
              </label>
            );
          })}
        </div>
      )}

      {universalWorlds.length > 0 && (
        <UniversalWeltenBlock
          worlds={universalWorlds}
          selected={universalSelected}
          onToggle={onToggleUniversal}
          prioStyle={prioStyle}
        />
      )}

      <div className="space-y-1.5">
        <Label htmlFor="setup-welt-custom">Eigenes Thema ergänzen</Label>
        <div className="flex gap-2">
          <Input
            id="setup-welt-custom"
            value={custom}
            onChange={(e) => setCustom(e.target.value)}
            placeholder="z. B. Umgang mit dem Hochdruckreiniger"
            data-testid="setup-welt-custom-input"
            onKeyDown={(e) => {
              if (e.key === "Enter" && custom.trim()) {
                e.preventDefault();
                onAddCustom(custom.trim());
                setCustom("");
              }
            }}
          />
          <Button
            type="button"
            variant="outline"
            disabled={!custom.trim()}
            onClick={() => {
              onAddCustom(custom.trim());
              setCustom("");
            }}
            data-testid="setup-welt-custom-add"
          >
            Hinzufügen
          </Button>
        </div>
      </div>

      <div className="text-xs text-slate-500" data-testid="setup-welten-count">
        {selectedCount === 0
          ? universalCount === 0
            ? "Bitte mindestens eine Welt auswählen, um das Setup abzuschließen."
            : ""
          : `${selectedCount} ${
              selectedCount === 1 ? "Welt wird" : "Welten werden"
            } parallel im Hintergrund erstellt (~30–90 s pro Welt).`}
        {universalCount > 0 && (
          <span data-testid="setup-universal-count">
            {selectedCount > 0 ? " " : ""}
            {universalCount} Universal-{universalCount === 1 ? "Welt wird" : "Welten werden"}{" "}
            sofort übernommen (fertig inkl. Lehrbilder, keine Wartezeit).
          </span>
        )}
      </div>
    </div>
  );
}

// Universal-Welten (Juni 2026): additiver Anzeige-Block in Schritt 4.
// Gleiche Zeilen-Optik wie die KI-Vorschläge (Titel, Kategorie, Risiko,
// Begründung) + Kennzeichnung „Sofort".
function UniversalWeltenBlock({ worlds, selected, onToggle, prioStyle }) {
  return (
    <div className="space-y-3 pt-2" data-testid="setup-universal-block">
      <div className="flex items-center gap-2 flex-wrap">
        <Library className="w-4 h-4 text-slate-700" />
        <h3 className="font-heading text-base font-semibold text-slate-900">
          Universal-Welten aus der CLartext-Bibliothek
        </h3>
        <Badge className="bg-emerald-100 text-emerald-800 hover:opacity-100 text-[10px] uppercase tracking-wider">
          sofort verfügbar
        </Badge>
      </div>
      <p className="text-xs text-slate-500" data-testid="setup-universal-hint">
        Geprüfte Standard-Welten, die in fast jedem Betrieb gebraucht werden. Sie
        werden ohne Wartezeit inkl. Lehrbilder in Ihren Betrieb übernommen – wählen
        Sie ab, was auf Sie nicht zutrifft (z. B. „Jugendliche im Betrieb" ohne
        Azubis). Auch diese Welten prüfen und geben Sie anschließend selbst frei.
      </p>
      <div className="space-y-2" data-testid="setup-universal-list">
        {worlds.map((w) => {
          const isSelected = !!selected[w.world_id];
          const slug = slugifyTestId(w.title);
          return (
            <label
              key={w.world_id}
              className={`flex items-start gap-3 px-3 py-3 rounded-md border transition-colors cursor-pointer ${
                isSelected
                  ? "border-slate-300 bg-slate-50"
                  : "border-slate-200 bg-white hover:bg-slate-50"
              }`}
              data-testid={`setup-universal-row-${slug}`}
            >
              <Checkbox
                checked={isSelected}
                onCheckedChange={() => onToggle?.(w.world_id)}
                className="mt-0.5"
                data-testid={`setup-universal-checkbox-${slug}`}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-slate-900">{w.title}</span>
                  {w.category && (
                    <Badge
                      variant="outline"
                      className="text-[10px] uppercase tracking-wider"
                    >
                      {w.category}
                    </Badge>
                  )}
                  {w.priority && (
                    <Badge
                      className={`${prioStyle(w.priority)} hover:opacity-100 text-[10px] uppercase tracking-wider`}
                    >
                      {w.priority}
                    </Badge>
                  )}
                  <Badge
                    className="bg-emerald-50 text-emerald-700 border border-emerald-200 hover:opacity-100 text-[10px] uppercase tracking-wider"
                    data-testid={`setup-universal-badge-${slug}`}
                  >
                    <Zap className="w-3 h-3 mr-1" />
                    Sofort
                  </Badge>
                </div>
                {w.reason && (
                  <div className="text-xs text-slate-500 mt-0.5">{w.reason}</div>
                )}
              </div>
            </label>
          );
        })}
      </div>
    </div>
  );
}

function slugifyTestId(s) {
  return (s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40);
}
